import { useState, useEffect, lazy, Suspense } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import React from "react";
import PlayerCardRow from "./PlayerCardRow";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useAuth } from "./AuthContext";
import Strider from "./images/Strider.png";
import Ohtani from "./images/Ohtani.png";
import { useParams } from "react-router-dom";
import MovementProfile from "./MovementProfile";
import ArsenalProfile from "./ArsenalProfile";
import HitterPitchProfile from "./HitterPitchProfile";
import Heatmap from "./Heatmap";
import BattedBallProfile from "./BattedBallProfile";
import SprayChart from "./SprayChart";
import RollingGraphs from "./RollingGraphs";
import EbayCards from "./EbayCards";
import LazyRender from "./utils/LazyRender";
import InfoCardTemp from "./InfoCardTemp";
import StatCardTemp from "./StatCardTemp";
import { KEY } from "./utils/ChartUtils";

const PitchHeatmap = lazy(() => import("./PitchHeatmap"));
const ReleaseProfile = lazy(() => import("./ReleaseProfile"));
const PitchSplits = lazy(() => import("./PitchSplits"));

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export default function PlayerPageTemp(props: any) {
  const { user } = useAuth();
  const isLocal = /^(localhost|127\.0\.0\.1|192\.168\.|10\.)/.test(window.location.hostname);
  const isPlus = isLocal || !!user?.is_plus;
  const plusHref = (href: string) => isPlus ? href : "/plus";
  const [playerData, setPlayerData] = useState<any>({});
  const [percentiles, setPercentiles] = useState<any>(null);
  const [playerInfo, setPlayerInfo] = useState<any>({});
  const [teamInfo, setTeamInfo] = useState<any>({});
  const { id } = useParams();
  const [seasonLevel, setSeasonLevel] = React.useState("2025_AAA");
  const [seasonLevels, setSeasonLevels] = React.useState<string[]>([]);

  const season = seasonLevel.split("_")[0];
  const level = seasonLevel.split("_")[1];

  const handleSeasonLevel = (event: SelectChangeEvent) => {
    setSeasonLevel(event.target.value as string);
  };

  const [qualifier, setQualifier] = React.useState("1");

  const handleQualifier = (event: SelectChangeEvent) => {
    setQualifier(event.target.value as string);
  };
  //console.log(season);

  const [ageRange, setAgeRange] = React.useState<number[]>([16, 40]);

  const handleAgeRange = (event: Event, newValue: any) => {
    setAgeRange(newValue);
  };

  const [pitchData, setPitchData] = React.useState<any[]>([]);
  const [pitches, setPitches] = React.useState<any[]>([]);
  const [armAngle, setArmAngle] = React.useState<Number>(0);
  const [splitData, setSplitData] = React.useState<any[]>([]);

  function parsePythonDict(input: string) {
    //console.log("INPUT", input)
    // Remove the BaseballLevel field entirely
    const startIdx = input.indexOf("BaseballLevel")-1;
    //console.log(startIdx)
    if (startIdx !== -2) {
      const endIdx = input.indexOf("',", startIdx + 1);
      //console.log(input.substring(0, startIdx))
      //console.log(input.substring(endIdx + 2))
      if (endIdx !== -1) {
        input = input.substring(0, startIdx) + input.substring(endIdx + 2);
      }
    }

    const startIdx2 = input.indexOf("HeightDisplay")-1;
    //console.log(startIdx2)
    if (startIdx2 !== -2) {
      const endIdx = input.indexOf("',", startIdx2 + 1);
      //console.log(input.substring(0, startIdx2))
      //console.log(input.substring(endIdx + 2))
      if (endIdx !== -1) {
        input = input.substring(0, startIdx2) + input.substring(endIdx + 2);
      }
    }
    //console.log(input)
    // Convert to valid JSON
    const jsonStr = input
      .replace(/'/g, '"')
      .replace(/None/g, 'null')
      .replace(/True/g, 'true')
      .replace(/False/g, 'false');

    return JSON.parse(jsonStr)
  }

  function reducePitchData(data: any[]) {
    const resultMap = new Map();
    data.forEach((item: any) => {
      const key = item.pitch_type;
      if (!resultMap.has(key)) {
        resultMap.set(key, {
          pitchName: item.pitch_name,
          pitchType: item.pitch_type,
          leftPercent: 0,
          rightPercent: 0
        });
      }
      const entry = resultMap.get(key);
      if (item.stand === 'L') {
        entry.leftPercent = -Math.abs(item.percentage);
      } else {
        entry.rightPercent = Math.abs(item.percentage);
      }
    });
    return Array.from(resultMap.values());
  }

  useEffect(() => {
    fetch(
      props.prod
        ? "https://oriolebird.pythonanywhere.com/player/" + id
        : "/player/" + id
    )
      .then((res) => res.json())
      .then((data1) => {
        const availableSeasonLevels = Object.keys(data1).reverse();
        setSeasonLevels(availableSeasonLevels);

        const currentSeasonLevel = availableSeasonLevels.includes(seasonLevel)
          ? seasonLevel
          : availableSeasonLevels[0];

        const [currentSeason, currentLevel] = currentSeasonLevel.split("_");

        if (!availableSeasonLevels.includes(seasonLevel)) {
          setSeasonLevel(availableSeasonLevels[0]);
        }

        const parsedPlayerInfo = parsePythonDict(data1[currentSeasonLevel]['player_info']);
        setPlayerData(data1[currentSeasonLevel]);
        setPlayerInfo(parsedPlayerInfo);
        setTeamInfo(parsePythonDict(data1[currentSeasonLevel]['team_info']));

        const position = data1[availableSeasonLevels[0]].Position === "P" ? "P" : "H";

        if (position === "P") {
          const height = parsedPlayerInfo.Height || 76;
          fetch(props.prod ? "https://oriolebird.pythonanywhere.com/pitches/" + id + '/' + currentSeason + '/' + height : "/pitches/" + id + '/' + currentSeason + '/' + height)
            .then((res) => res.json())
            .then((pData) => {
              setPitchData(pData);
              const p = Object.keys(pData.reduce((r: any, { pitch_type }: any) => { r[pitch_type] = ''; return r; }, {})).map((pitch: string) => KEY[pitch]);
              setPitches(p);
              setSplitData(reducePitchData(pData));
              setArmAngle(pData.length > 0 ? pData[0].avg_arm_angle.toFixed(1) : 0);
            });
        }

        fetch(
          props.prod
            ? "https://oriolebird.pythonanywhere.com/get_player_percentiles/" +
            id + "/" + position + "/" + currentLevel + "/" + currentSeason + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1]
            : "/get_player_percentiles/" +
            id + "/" + position + "/" + currentLevel + "/" + currentSeason + "/" + qualifier + "/" + ageRange[0] + "/" + ageRange[1]
        )
          .then((res) => res.json())
          .then((data) => {
            setPercentiles(data.data[0]);
          });
      });
  }, [id, props.prod, seasonLevel, ageRange, qualifier]);
  //console.log("PD:", playerData);
  //console.log("pi:", playerInfo);
  //console.log(percentiles)
  return (
    <div>

      {playerData !== undefined && playerData !== null && (
        <div style={{ marginBottom: "10px" }}>
          <title>{playerData.name === undefined ? "Prospect Savant" : playerData.name + " - Prospect Savant"}</title>

          <Grid container justifyContent="center" spacing={2} maxWidth="100%">
            <Grid item maxWidth="90vw">
              <Paper
                sx={{
                  height: "100%",
                  maxWidth: "90vw",
                  width: 500,
                  border: "#293241 solid 2px",
                  backgroundColor: "#FFFFFF",
                  boxShadow:
                    "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                }}
                square
                variant="outlined"
              >
                {playerInfo.UPURL && <InfoCardTemp playerInfoProp={playerInfo} teamInfoProp={teamInfo} idProp={id} playerDataProp={playerData} />}
              </Paper>
            </Grid>
            <Grid item maxWidth="90vw">
              {playerData.xwoba_p !== "undefined" &&
                percentiles !== null &&
                !isNaN(playerData.xwoba_p) && (
                  <Paper
                    sx={{
                      maxWidth: "90vw",
                      height: "100%",
                      width: 500,
                      border: "#293241 solid 2px",
                      boxShadow:
                        "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                    }}
                    square
                    variant="outlined"
                  >
                    {playerData.Position === "P" && (
                      <div>
                        <Grid
                          container
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                          style={{
                            backgroundColor: playerData.MLB_AbbName
                              ? teamColors[playerData.MLB_AbbName][0]
                              : teamColors["FA"][0],
                            paddingLeft: "10px",
                            paddingBottom: "10px",
                            marginBottom: "10px",
                            wordWrap: "break-word",
                          }}
                        >
                          <img
                            src={Strider}
                            alt=""
                            style={{
                              marginLeft: "10px",
                              marginTop: "10px",
                              width: props.isDesktop ? "64px" : "36px",
                              maxWidth: "12vw",
                            }}
                          />
                          <FormControl
                            variant="standard"
                            sx={{ paddingLeft: "15px", paddingTop: "8px" }}
                          >
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={seasonLevel}
                              label="Season/Level"
                              onChange={handleSeasonLevel}
                              defaultValue="2025_AAA"
                              disableUnderline={true}
                              sx={{
                                height: "2.5rem",
                                color: playerData.MLB_AbbName
                                  ? teamColors[playerData.MLB_AbbName][1]
                                  : teamColors["FA"][1],
                                fontSize: props.isDesktop ? "18pt" : "14pt",
                                fontWeight: "bold",
                                "& .MuiSelect-root": {
                                  borderColor: "white",
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                  borderBottom: "1px solid white",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                },
                              }}
                            >
                              {seasonLevels.map((sl) => (
                                <MenuItem key={sl} value={sl}>{sl.replace("_", " ")}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span
                            style={{
                              marginLeft: "5px",
                              paddingTop: "8px",
                              fontSize: props.isDesktop ? "17pt" : "11pt",
                              fontWeight: "bold",
                              color: playerData.MLB_AbbName
                                ? teamColors[playerData.MLB_AbbName][1]
                                : teamColors["FA"][1],
                            }}
                          >
                            {" "}
                            Pitching Percentiles
                          </span>
                          {[{href: `/mlb-context/${id}`, label: "MLB Context", gated: true}, {href: `/plus/splits/${id}`, label: "Splits", gated: true}, ...(playerData.Position === "C" ? [{href: "/plus/framing/", label: "Framing", gated: true}] : [])].map(({href, label, gated}) => (
                            <a
                              key={label}
                              href={gated ? plusHref(href) : href}
                              style={{
                                marginLeft: "8px",
                                padding: "2px 8px",
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                border: "1.5px solid " + (playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0]),
                                color: playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0],
                                backgroundColor: "#fff",
                                textDecoration: "none",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              {gated && !isPlus && <LockIcon sx={{ fontSize: "0.7rem" }} />}
                              {label} ↗
                            </a>
                          ))}
                        </Grid>
                        <div>
                          <div
                            style={{ margin: "auto", maxWidth: "fit-content" }}
                          >
                            <FormControl>
                              <InputLabel id="demo-simple-select-label">
                                Pitch Qualifier
                              </InputLabel>
                              <Select
                                value={qualifier}
                                label="Pitch Qualifier"
                                onChange={handleQualifier}
                                defaultValue="1"
                                sx={{ width: "102px" }}
                              >
                                <MenuItem value={1}>1</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={150}>150</MenuItem>
                                <MenuItem value={200}>200</MenuItem>
                                <MenuItem value={250}>250</MenuItem>
                                <MenuItem value={300}>300</MenuItem>
                                <MenuItem value={400}>400</MenuItem>
                                <MenuItem value={500}>500</MenuItem>
                                <MenuItem value={600}>600</MenuItem>
                                <MenuItem value={700}>700</MenuItem>
                                <MenuItem value={800}>800</MenuItem>
                                <MenuItem value={900}>900</MenuItem>
                                <MenuItem value={1000}>1000</MenuItem>
                                <MenuItem value={1250}>1250</MenuItem>
                                <MenuItem value={1500}>1500</MenuItem>
                              </Select>
                            </FormControl>
                            <span
                              style={{
                                position: "absolute",
                                marginLeft: props.isDesktop ? "25px" : "15px",
                                marginTop: "3px",
                                color: "#5A6465",
                                fontSize: 16,
                              }}
                            >
                              Age Range
                            </span>
                            <Slider
                              getAriaLabel={() => "Age Range"}
                              value={ageRange}
                              onChange={handleAgeRange}
                              valueLabelDisplay="on"
                              min={16}
                              max={40}
                              disableSwap={true}
                              sx={{
                                width: "200px",
                                marginLeft: props.isDesktop ? "30px" : "20px",
                                marginTop: "25px",
                                "& .MuiSlider-valueLabel": {
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  marginTop: "33px",
                                  backgroundColor: "unset",
                                  color: "#FFFFFF",
                                  "&::before": {
                                    display: "none",
                                  },
                                  "& *": {
                                    background: "transparent",
                                    color: "#FFFFFF",
                                  },
                                },
                              }}
                            />
                          </div>
                          <PlayerCardRow
                            stat="PS Score"
                            value={(playerData.score_p * 100).toFixed(2).toString()}
                            percentile={percentiles?.score_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.score_p.toFixed(2)}
                            isDesktop={props.isDesktop}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              marginTop: "-5px",
                              color: "rgba(0, 0, 0, 0.4)",
                              fontWeight: 600,
                              fontSize: "0.5rem",
                              pointerEvents: "none",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              letterSpacing: "0.5em", // tweak this value until it fills ~80%
                            }}
                          >
                            ProspectSavant.com/player/{id}
                          </Typography>
                          <PlayerCardRow
                            stat="xwOBA"
                            value={playerData.xwoba.toFixed(3)}
                            percentile={percentiles?.xwoba_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xwoba_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="xBA"
                            value={playerData.xba.toFixed(3)}
                            percentile={percentiles?.xba_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xba_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="xSLG"
                            value={playerData.xslg.toFixed(3)}
                            percentile={percentiles?.xslg_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xslg_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="EV"
                            value={
                              playerData.ev + (props.isDesktop ? " mph" : "")
                            }
                            percentile={percentiles?.ev_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.ev_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Barrel %"
                            value={playerData.barrelbbe + "%"}
                            percentile={
                              percentiles?.barrelbbe_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.barrelbbe_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Hard-Hit%"
                            value={playerData.hhrate + "%"}
                            percentile={percentiles?.hhrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.hhrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="K %"
                            value={playerData.krate + "%"}
                            percentile={percentiles?.krate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.krate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="BB %"
                            value={playerData.bbrate + "%"}
                            percentile={percentiles?.bbrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.bbrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Chase %"
                            value={playerData.chaserate.toFixed(2) + "%"}
                            percentile={
                              percentiles?.chaserate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.chaserate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Whiff %"
                            value={playerData.whiffrate + "%"}
                            percentile={
                              percentiles?.whiffrate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.whiffrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {level === "AAA" && (
                            <PlayerCardRow
                              stat="Z-Swing %"
                              value={playerData.zswing.toFixed(2) + "%"}
                              percentile={
                                percentiles?.zswing_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zswing_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          {level === "AAA" && (
                            <PlayerCardRow
                              stat="Z-Contact %"
                              value={
                                playerData.zcontact.toFixed(2) + "%"
                              }
                              percentile={
                                percentiles?.zcontact_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zcontact_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="SwStr %"
                            value={playerData.swstr.toFixed(2) + "%"}
                            percentile={percentiles?.swstr_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.swstr_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />


                        </div>
                      </div>
                    )}
                    {playerData.Position !== "P" && (
                      <div>
                        <Grid
                          container
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                          style={{
                            backgroundColor: playerData.MLB_AbbName
                              ? teamColors[playerData.MLB_AbbName][0]
                              : teamColors["FA"][0],
                            paddingLeft: "10px",
                            paddingBottom: "10px",
                            marginBottom: "10px",
                            wordWrap: "break-word",
                          }}
                        >
                          <img
                            src={Ohtani}
                            height={props.isDesktop ? "64px" : "42px"}
                            alt=""
                            style={{ marginLeft: "10px", marginTop: "10px" }}
                          />
                          <FormControl
                            variant="standard"
                            sx={{ paddingLeft: "15px", paddingTop: "12px" }}
                          >
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={seasonLevel}
                              label="Season/Level"
                              onChange={handleSeasonLevel}
                              defaultValue="2025_AAA"
                              disableUnderline={true}
                              sx={{
                                height: "2.5rem",
                                color: playerData.MLB_AbbName
                                  ? teamColors[playerData.MLB_AbbName][1]
                                  : teamColors["FA"][1],
                                fontSize: props.isDesktop ? "18pt" : "15pt",
                                fontWeight: "bold",
                                "& .MuiSelect-root": {
                                  borderColor: "white",
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                  borderBottom: "1px solid white",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                },
                              }}
                            >
                              {seasonLevels.map((sl) => (
                                <MenuItem key={sl} value={sl}>{sl.replace("_", " ")}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span
                            style={{
                              marginLeft: "5px",
                              fontSize: props.isDesktop ? "18pt" : "10pt",
                              fontWeight: "bold",
                              marginTop: "12px",
                              color: playerData.MLB_AbbName
                                ? teamColors[playerData.MLB_AbbName][1]
                                : teamColors["FA"][1],
                            }}
                          >
                            Hitting Percentiles
                          </span>
                          {[{href: `/mlb-context/${id}`, label: "MLB Context", gated: true}, {href: `/mlb-comparison/${id}`, label: "MLB Comps", gated: true}, {href: `/plus/splits/${id}`, label: "Splits", gated: true}, ...(playerData.Position === "C" ? [{href: "/plus/framing/", label: "Framing", gated: true}] : [])].map(({href, label, gated}) => (
                            <a
                              key={label}
                              href={gated ? plusHref(href) : href}
                              style={{
                                marginLeft: "8px",
                                padding: "2px 8px",
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                border: "1.5px solid " + (playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0]),
                                color: playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0],
                                backgroundColor: "#fff",
                                textDecoration: "none",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              {gated && !isPlus && <LockIcon sx={{ fontSize: "0.7rem" }} />}
                              {label} ↗
                            </a>
                          ))}
                        </Grid>
                        <div>
                          <div
                            style={{ margin: "auto", maxWidth: "fit-content" }}
                          >
                            <FormControl>
                              <InputLabel id="demo-simple-select-label">
                                Pitch Qualifier
                              </InputLabel>
                              <Select
                                value={qualifier}
                                label="Pitch Qualifier"
                                onChange={handleQualifier}
                                defaultValue="1"
                                sx={{ width: "102px" }}
                              >
                                <MenuItem value={1}>1</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={150}>150</MenuItem>
                                <MenuItem value={200}>200</MenuItem>
                                <MenuItem value={250}>250</MenuItem>
                                <MenuItem value={300}>300</MenuItem>
                                <MenuItem value={400}>400</MenuItem>
                                <MenuItem value={500}>500</MenuItem>
                                <MenuItem value={600}>600</MenuItem>
                                <MenuItem value={700}>700</MenuItem>
                                <MenuItem value={800}>800</MenuItem>
                                <MenuItem value={900}>900</MenuItem>
                                <MenuItem value={1000}>1000</MenuItem>
                                <MenuItem value={1250}>1250</MenuItem>
                                <MenuItem value={1500}>1500</MenuItem>
                              </Select>
                            </FormControl>
                            <span
                              style={{
                                position: "absolute",
                                marginLeft: props.isDesktop ? "25px" : "15px",
                                marginTop: "3px",
                                color: "#5A6465",
                                fontSize: 16,
                              }}
                            >
                              Age Range
                            </span>
                            <Slider
                              getAriaLabel={() => "Age Range"}
                              value={ageRange}
                              onChange={handleAgeRange}
                              valueLabelDisplay="on"
                              min={16}
                              max={40}
                              disableSwap={true}
                              sx={{
                                width: "200px",
                                marginLeft: props.isDesktop ? "30px" : "20px",
                                marginTop: "25px",
                                "& .MuiSlider-valueLabel": {
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  marginTop: "33px",
                                  backgroundColor: "unset",
                                  color: "#FFFFFF",
                                  "&::before": {
                                    display: "none",
                                  },
                                  "& *": {
                                    background: "transparent",
                                    color: "#FFFFFF",
                                  },
                                },
                              }}
                            />
                          </div>
                          <PlayerCardRow
                            stat="PS Score"
                            value={(playerData.score_p * 100).toFixed(2).toString()}
                            percentile={percentiles?.score_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.score_p}
                            isDesktop={props.isDesktop}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              marginTop: "-5px",
                              color: "rgba(0, 0, 0, 0.4)",
                              fontWeight: 600,
                              fontSize: "0.5rem",
                              pointerEvents: "none",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              letterSpacing: "0.5em", // tweak this value until it fills ~80%
                            }}
                          >
                            ProspectSavant.com/player/{id}
                          </Typography>
                          <PlayerCardRow
                            stat="xwOBA"
                            value={playerData.xwoba.toFixed(3)}
                            percentile={percentiles?.xwoba_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xwoba_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="xBA"
                            value={playerData.xba.toFixed(3)}
                            percentile={percentiles?.xba_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xba_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="xSLG"
                            value={playerData.xslg.toFixed(3)}
                            percentile={percentiles?.xslg_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.xslg_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="EV"
                            value={
                              playerData.ev + (props.isDesktop ? " mph" : "")
                            }
                            percentile={percentiles?.ev_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.ev_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {level === "AAA" && (
                            <PlayerCardRow
                              stat="Max EV"
                              value={
                                playerData.maxev +
                                (props.isDesktop ? " mph" : "")
                              }
                              percentile={percentiles?.maxev_p.toFixed(2) * 100}
                              percentile_raw={percentiles?.maxev_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="50th% EV"
                            value={
                              playerData.ev50 + (props.isDesktop ? " mph" : "")
                            }
                            percentile={percentiles?.ev50_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.ev50_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="90th% EV"
                            value={
                              playerData.ev90 + (props.isDesktop ? " mph" : "")
                            }
                            percentile={percentiles?.ev90_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.ev90_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Barrel %"
                            value={playerData.barrelbbe + "%"}
                            percentile={
                              percentiles?.barrelbbe_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.barrelbbe_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Hard-Hit%"
                            value={playerData.hhrate + "%"}
                            percentile={percentiles?.hhrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.hhrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="K %"
                            value={playerData.krate + "%"}
                            percentile={percentiles?.krate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.krate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="BB %"
                            value={playerData.bbrate + "%"}
                            percentile={percentiles?.bbrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.bbrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Chase %"
                            value={playerData.chaserate.toFixed(2) + "%"}
                            percentile={
                              percentiles?.chaserate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.chaserate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Whiff %"
                            value={playerData.whiffrate + "%"}
                            percentile={
                              percentiles?.whiffrate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.whiffrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Pull Air %"
                            value={(playerData.pullair).toFixed(2) + "%"}
                            percentile={
                              percentiles?.pullair_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.pullair_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Swing %"
                            value={playerData.swing.toFixed(2) + "%"}
                            percentile={
                              percentiles?.swing_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.swing_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {level === "AAA" && (
                            <PlayerCardRow
                              stat="Z-Swing %"
                              value={playerData.zswing.toFixed(2) + "%"}
                              percentile={
                                percentiles?.zswing_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zswing_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          {level === "AAA" && (
                            <PlayerCardRow
                              stat="Z-Contact %"
                              value={
                                playerData.zcontact.toFixed(2) + "%"
                              }
                              percentile={
                                percentiles?.zcontact_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zcontact_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="SwStr %"
                            value={playerData.swstr.toFixed(2) + "%"}
                            percentile={percentiles?.swstr_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.swstr_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Spd"
                            value={playerData.spd.toFixed(2)}
                            percentile={percentiles?.spd_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.spd_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="wBsR/PA"
                            value={playerData.wbsr_pa.toFixed(4)}
                            percentile={percentiles?.wbsr_pa_p * 100}
                            percentile_raw={percentiles?.wbsr_pa_p}
                            isDesktop={props.isDesktop}
                          />
                        </div>
                      </div>
                    )}
                  </Paper>
                )}
            </Grid>
            {true && (
              <Grid item maxWidth="90vw">
                <Paper
                  sx={{
                    maxWidth: "90vw",
                    height: "100%",
                    width: 500,
                    border: "#293241 solid 2px",
                    boxShadow:
                      "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                  }}
                  square
                  variant="outlined"
                >
                  {playerInfo.UPURL && (
                    <StatCardTemp
                      playerDataProp={playerData}
                      playerInfoProp={playerInfo}
                      isDesktop={props.isDesktop}
                      season={season}
                      teamInfoProp={teamInfo}
                    />
                  )} 
                </Paper>
              </Grid>
            )}
            {playerData.Position === "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <ArsenalProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                />
              </Grid>
            )}
            {playerData.Position === "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <MovementProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  height={playerInfo.Height}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  playerDataProp={playerData}
                  pitchData={pitchData}
                  pitches={pitches}
                />
              </Grid>
            )}
            {playerData.Position === "P" && playerInfo.UPURL && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchHeatmap
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    playerDataProp={playerData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && playerInfo.UPURL && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <ReleaseProfile
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    height={playerInfo.Height}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    playerDataProp={playerData}
                    armAngle={armAngle}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && playerInfo.UPURL && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchSplits
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    height={playerInfo.Height}
                    playerInfoProp={playerInfo}
                    splitData={splitData}
                    playerDataProp={playerData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position !== "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <HitterPitchProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  level={level}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <Heatmap
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  level={level}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <BattedBallProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                  teamInfoProp={teamInfo}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <SprayChart
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  playerData={playerData}
                />
              </Grid>
            )}
            {playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <RollingGraphs
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  playerData={playerData}
                  type={playerData.Position === "P" ? "pitcher" : "batter"}
                />
              </Grid>
            )}
            {playerInfo.UPURL && (
              <Grid item maxWidth="90vw">
                <EbayCards
                  isDesktop={props.isDesktop}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  teamInfoProp={teamInfo}
                  playerData={playerData}
                />
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
}
